import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, DollarSign, Users, Briefcase, ArrowRight, Search, Filter, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const mockJobs = [
  {
    id: '1',
    title: 'Senior Full Stack Developer',
    experience: '5-8 years',
    salary: '$80,000 - $120,000',
    location: 'Nagpur, Maharashtra',
    remote: true,
    country: 'India',
    type: 'Full-time',
    description: 'We are looking for a Senior Full Stack Developer with expertise in React, Node.js, and cloud technologies to join our growing team.',
    requirements: ['React.js, Node.js', 'Cloud platforms (AWS/Azure)', 'MongoDB, PostgreSQL', 'Git, Docker'],
    posted_date: '2025-01-15T00:00:00.000Z'
  },
  {
    id: '2',
    title: 'AI/ML Engineer',
    experience: '3-6 years',
    salary: '$70,000 - $100,000',
    location: 'Remote',
    remote: true,
    country: 'Global',
    type: 'Full-time',
    description: 'Join our AI team to develop cutting-edge machine learning solutions for healthcare and fintech applications.',
    requirements: ['Python, TensorFlow/PyTorch', 'Machine Learning algorithms', 'Data preprocessing', 'MLOps experience'],
    posted_date: '2025-01-10T00:00:00.000Z'
  },
  {
    id: '3',
    title: 'DevOps Engineer',
    experience: '4-7 years',
    salary: '$75,000 - $110,000',
    location: 'Hybrid',
    remote: true,
    country: 'India/US',
    type: 'Full-time',
    description: 'Seeking a DevOps Engineer to manage our cloud infrastructure and implement CI/CD pipelines across multiple projects.',
    requirements: ['AWS/Azure/GCP', 'Docker, Kubernetes', 'Jenkins, GitLab CI', 'Infrastructure as Code'],
    posted_date: '2025-01-08T00:00:00.000Z'
  },
  {
    id: '4',
    title: 'Frontend Developer',
    experience: '2-4 years',
    salary: '$50,000 - $75,000',
    location: 'Nagpur, Maharashtra',
    remote: false,
    country: 'India',
    type: 'Full-time',
    description: 'Looking for a creative Frontend Developer to build responsive and user-friendly web applications.',
    requirements: ['React.js, JavaScript', 'HTML5, CSS3, Tailwind', 'Responsive design', 'RESTful APIs'],
    posted_date: '2025-01-12T00:00:00.000Z'
  }
];

const CareersPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    jobTitle: '',
    city: '',
    country: '',
    experience: '',
    type: '',
    remote: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Application Modal State
  const [selectedJob, setSelectedJob] = useState(null);
  const [isApplicationOpen, setIsApplicationOpen] = useState(false);
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    mandatorySkills: '',
    summary: '',
    resume: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchJobs = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      // Fallback to mock data if API fails
      setJobs(mockJobs);
      setLoading(false);
    }
  }, []);

  const applyFilters = React.useCallback(() => {
    let filtered = jobs.filter(job => {
      const matchesTitle = !filters.jobTitle || job.title.toLowerCase().includes(filters.jobTitle.toLowerCase());
      const matchesCity = !filters.city || job.location.toLowerCase().includes(filters.city.toLowerCase());
      const matchesCountry = !filters.country || job.country.toLowerCase().includes(filters.country.toLowerCase());
      const matchesExperience = !filters.experience || job.experience.toLowerCase().includes(filters.experience.toLowerCase());
      const matchesType = !filters.type || job.type === filters.type;
      const matchesRemote = filters.remote === '' || 
        (filters.remote === 'yes' && job.remote) || 
        (filters.remote === 'no' && !job.remote);
      
      return matchesTitle && matchesCity && matchesCountry && matchesExperience && matchesType && matchesRemote;
    });
    
    setFilteredJobs(filtered);
  }, [jobs, filters]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      jobTitle: '',
      city: '',
      country: '',
      experience: '',
      type: '',
      remote: ''
    });
  };

  const handleApply = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    
    // Parse requirements to extract mandatory skills
    // Assuming requirements is an array of strings like ['React.js, Node.js', 'Cloud platforms...']
    // We'll join them and let the user edit/confirm
    let initialMandatorySkills = '';
    if (job && job.requirements) {
      initialMandatorySkills = job.requirements.join(', ');
    }

    setSelectedJob(job);
    setApplicationForm(prev => ({
      ...prev,
      mandatorySkills: initialMandatorySkills
    }));
    setIsApplicationOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApplicationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setApplicationForm(prev => ({
        ...prev,
        resume: file
      }));
    }
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    if (!applicationForm.resume) {
      alert("Please upload your resume");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('name', applicationForm.name);
      formData.append('email', applicationForm.email);
      formData.append('phone', applicationForm.phone);
      formData.append('experience', applicationForm.experience);
      formData.append('mandatory_skills', applicationForm.mandatorySkills);
      formData.append('summary', applicationForm.summary);
      formData.append('resume', applicationForm.resume);

      await axios.post(`${API}/jobs/${selectedJob.id}/apply`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      alert("Application submitted successfully! The recruiter will be notified.");
      setIsApplicationOpen(false);
      setApplicationForm({
        name: '',
        email: '',
        phone: '',
        experience: '',
        mandatorySkills: '',
        summary: '',
        resume: null
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      alert("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/30 to-cyan-50/30">
        <Header />
        <div className="pt-24 flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <p className="ml-4 text-gray-600">Loading job opportunities...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/30 to-cyan-50/30 careers-page-typography">
      <style>
        {`
          .careers-page-typography {
            font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          }
          .careers-page-typography h1, .careers-page-typography h2, .careers-page-typography h3 {
            font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            font-weight: 700;
          }
        `}
      </style>
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="text-transparent bg-gradient-to-r from-purple-600 via-cyan-500 to-violet-500 bg-clip-text animate-pulse">
                Career Opportunities
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Join our innovative team and build the future of technology with Tripod Technologies
            </p>
          </div>

          {/* Filters */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {filteredJobs.length} Job{filteredJobs.length !== 1 ? 's' : ''} Available
              </h2>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className="border-purple-300 text-purple-600 hover:bg-purple-50"
              >
                <Filter className="w-4 h-4 mr-2" />
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
            </div>

            {showFilters && (
              <Card className="mb-6 bg-white/95 backdrop-blur-sm border border-cyan-200/50">
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                      <input
                        type="text"
                        value={filters.jobTitle}
                        onChange={(e) => handleFilterChange('jobTitle', e.target.value)}
                        placeholder="e.g. Developer"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={filters.city}
                        onChange={(e) => handleFilterChange('city', e.target.value)}
                        placeholder="e.g. Nagpur"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                      <input
                        type="text"
                        value={filters.country}
                        onChange={(e) => handleFilterChange('country', e.target.value)}
                        placeholder="e.g. India"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Experience</label>
                      <input
                        type="text"
                        value={filters.experience}
                        onChange={(e) => handleFilterChange('experience', e.target.value)}
                        placeholder="e.g. 2-4 years"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
                      <select
                        value={filters.type}
                        onChange={(e) => handleFilterChange('type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      >
                        <option value="">All Types</option>
                        <option value="Full-time">Full-time</option>
                        <option value="Part-time">Part-time</option>
                        <option value="Contract">Contract</option>
                        <option value="Internship">Internship</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Remote</label>
                      <select
                        value={filters.remote}
                        onChange={(e) => handleFilterChange('remote', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      >
                        <option value="">All</option>
                        <option value="yes">Remote</option>
                        <option value="no">On-site</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4 flex space-x-3">
                    <Button
                      onClick={clearFilters}
                      variant="outline"
                      size="sm"
                      className="border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Job Listings */}
          <div className="space-y-6">
            {filteredJobs.length === 0 ? (
              <Card className="text-center py-12 bg-white/95 backdrop-blur-sm">
                <CardContent>
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No jobs match your current filters</p>
                  <Button
                    onClick={clearFilters}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              filteredJobs.map((job) => (
                <Card
                  key={job.id}
                  className="group hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300 border-0 shadow-lg hover:-translate-y-2 transform-gpu bg-white/95 backdrop-blur-sm border border-cyan-200/50"
                >
                  <CardHeader>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-mono rounded-full">
                            {job.job_id || `JOB-${job.id?.slice(0, 8).toUpperCase()}`}
                          </span>
                          <a
                            href={`/jobs/${job.job_id || job.id}`}
                            className="text-xs text-purple-600 underline"
                          >
                            View job page
                          </a>
                        </div>
                        <CardTitle className="text-xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-2">
                          {job.title}
                        </CardTitle>
                        <CardDescription className="text-gray-600">
                          Posted on {formatDate(job.posted_date)}
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => handleApply(job.id)}
                        className="mt-4 lg:mt-0 bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/50 transition-all duration-300 transform hover:scale-105"
                      >
                        Apply Now
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="flex items-center text-sm text-gray-600">
                        <Briefcase className="w-4 h-4 text-purple-500 mr-2" />
                        <span>{job.experience}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <DollarSign className="w-4 h-4 text-cyan-500 mr-2" />
                        <span>{job.salary}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-purple-500 mr-2" />
                        <span>{job.location}</span>
                        {job.remote && (
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Remote
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="w-4 h-4 text-cyan-500 mr-2" />
                        <span>{job.type}</span>
                      </div>
                    </div>

                    <div className="mb-6">
                      <p className="text-gray-700 leading-relaxed mb-4">
                        {job.description}
                      </p>
                      
                      <div>
                        <h4 className="font-semibold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-2">Requirements:</h4>
                        <div className="flex flex-wrap gap-2">
                          {job.requirements && job.requirements.map((req, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-gradient-to-r from-purple-100 to-cyan-100 text-purple-700 text-sm rounded-full border border-purple-200"
                            >
                              {req}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <Users className="w-4 h-4 mr-2" />
                          <span className="font-medium">Country: {job.country}</span>
                        </div>
                        
                        {/* Recruiter Contact Info */}
                        {job.recruiter && (
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                            <span className="text-purple-600 font-medium">Contact:</span>
                            <span>{job.recruiter.name}</span>
                            <a href={`mailto:${job.recruiter.email}`} className="text-cyan-600 hover:underline">
                              {job.recruiter.email}
                            </a>
                            <a href={`tel:${job.recruiter.phone}`} className="text-cyan-600 hover:underline">
                              {job.recruiter.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </main>

      <Footer />
      
      {/* Application Modal */}
      <Dialog open={isApplicationOpen} onOpenChange={setIsApplicationOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Apply for {selectedJob?.title}
            </DialogTitle>
            <DialogDescription>
              Please fill in your details below to apply for this position.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleApplicationSubmit} className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={applicationForm.name}
                  onChange={handleInputChange}
                  required
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={applicationForm.email}
                  onChange={handleInputChange}
                  required
                  placeholder="john@example.com"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Contact Number *</Label>
              <Input
                id="phone"
                name="phone"
                value={applicationForm.phone}
                onChange={handleInputChange}
                required
                placeholder="+91 98765 43210"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="experience">Experience in required skills *</Label>
              <Textarea
                id="experience"
                name="experience"
                value={applicationForm.experience}
                onChange={handleInputChange}
                required
                placeholder="Briefly describe your experience with the required skills..."
                className="min-h-[80px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mandatorySkills">Mandatory Skills & Experience (Years) *</Label>
              <div className="text-xs text-gray-500 mb-1">
                Please specify your years of experience for each skill listed below.
              </div>
              <Textarea
                id="mandatorySkills"
                name="mandatorySkills"
                value={applicationForm.mandatorySkills}
                onChange={handleInputChange}
                required
                placeholder="E.g., React (3 years), Node.js (2 years)..."
                className="min-h-[100px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="summary">Professional Summary *</Label>
              <Textarea
                id="summary"
                name="summary"
                value={applicationForm.summary}
                onChange={handleInputChange}
                required
                placeholder="Tell us why you're a good fit for this role..."
                className="min-h-[100px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="resume">Resume/CV (PDF/Word) *</Label>
              <div className="flex items-center justify-center w-full">
                <label
                  htmlFor="resume"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 border-gray-300 hover:border-purple-500 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-3 text-gray-400" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      {applicationForm.resume ? applicationForm.resume.name : "PDF or DOCX (MAX. 5MB)"}
                    </p>
                  </div>
                  <input
                    id="resume"
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    required={!applicationForm.resume}
                  />
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsApplicationOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-purple-600 hover:bg-purple-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CareersPage;
